import { formatApiError } from "@/lib/api";

// 183: poloha pro přihlášení do práce. Přednost má přesné GPS; když ho telefon
// nestihne (uvnitř budovy, Xiaomi s úsporou baterie), vezme se poloha ze sítě
// (Wi-Fi / BTS) nebo nedávno zjištěná — na kontrolu „je na stavbě" stačí.
let lastError = null;   // "denied" | "unavailable" | "timeout" | "unsupported" | null

const once = (opts) => new Promise((resolve) => {
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ pos }),
    (err) => resolve({ err }),
    opts);
});

// 215: polohu lze „předehřát“ už při podržení tlačítka příchodu/odchodu — za 0,9 s podržení
// a přechod na Přehled ji telefon často stihne najít. Jen když je poloha už povolená (žádný
// nečekaný dotaz na oprávnění).
const WARM_MS = 20000;
let warm = null;   // { at, promise }
export function warmGps() {
  if (typeof navigator === "undefined" || !navigator.geolocation || !navigator.permissions?.query) return;
  if (warm && Date.now() - warm.at < WARM_MS) return;
  navigator.permissions.query({ name: "geolocation" }).then((p) => {
    if (p.state === "granted" && !(warm && Date.now() - warm.at < WARM_MS)) {
      warm = { at: Date.now(), promise: once({ enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }) };
    }
  }).catch(() => { /* prohlížeč dotaz na oprávnění nezná */ });
}

const after = (ms) => new Promise((resolve) => setTimeout(() => resolve(null), ms));

// maxMs: kde poloha není povinná (odchod, konec dne), nečekat na ni déle — bez ní se pokračuje.
export async function captureGps({ maxMs } = {}) {
  if (!maxMs) return locateNow();
  const r = await Promise.race([locateNow(), after(maxMs)]);
  if (r) return r;
  lastError = "timeout";
  return { lat: null, lng: null, has_gps: false };
}

async function locateNow() {
  lastError = null;
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    lastError = "unsupported";
    return { lat: null, lng: null, has_gps: false };
  }
  // 215: přesné GPS a poloha ze sítě (Wi-Fi / BTS) běží zároveň — dřív se na síť čekalo
  // až po 10 s marného hledání GPS. Přesná má přednost: když síťová přijde dřív, počká se
  // na přesnou ještě nejvýš 3 s. Celkem nejvýš ~10 s místo 20 s.
  const pre = warm && Date.now() - warm.at < WARM_MS ? warm.promise : null;
  warm = null;
  const precise = pre || once({ enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
  const coarse = once({ enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 });
  const first = await Promise.race([precise.then((r) => ({ precise: true, r })), coarse.then((r) => ({ precise: false, r }))]);
  let r = first.r;
  if (r.err?.code !== 1) {
    if (first.precise) {
      if (!r.pos) r = await coarse;                       // přesná selhala → síťová
    } else {
      const p = await Promise.race([precise, after(3000)]);
      if (p?.pos) r = p;                                  // přesná stihla do 3 s
      else if (!r.pos) r = await precise;                 // síťová selhala → počkat na přesnou
    }
  }
  if (r.pos) return { lat: r.pos.coords.latitude, lng: r.pos.coords.longitude, has_gps: true };
  lastError = r.err?.code === 1 ? "denied" : r.err?.code === 3 ? "timeout" : "unavailable";
  return { lat: null, lng: null, has_gps: false };
}

export function gpsHelp(reason = lastError) {
  if (reason === "denied") {
    return "Poloha je pro Proyekty zakázaná. Android (Xiaomi): Nastavení › Aplikace › Spravovat aplikace › Chrome "
      + "(nebo Proyekty) › Oprávnění aplikace › Poloha › Povolit jen při používání. V Chrome navíc: zámek vlevo od adresy "
      + "› Oprávnění › Poloha › Povolit. iPhone: Nastavení › Soukromí › Polohové služby › Safari / Proyekty › Při používání.";
  }
  if (reason === "unsupported") return "Tento prohlížeč polohu nepodporuje. Otevřete Proyekty v Chrome nebo Safari.";
  return "Telefon polohu nenašel. Zapněte Polohu (stáhněte horní lištu a klepněte na ikonu Poloha / GPS), "
    + "případně vyjděte ven nebo k oknu a zkuste to znovu.";
}

// Chyba z API: u povinné polohy (428) konkrétní rada podle toho, proč poloha chybí.
export function attendanceError(e) {
  if (e?.response?.status === 428) return gpsHelp();
  return formatApiError(e?.response?.data?.detail);
}
